import React, { useState } from "react"
import TextButton from "../../atoms/buttons/TextButton"
import { AnimatePresence } from "framer-motion"
import Modal from "../Modals/Modal"
import { createPortal } from "react-dom"
import { useRequestDemo } from "../../../hooks/useRequestDemo"
import { RequestDemoInput } from "../../../graphql/mutations/requestDemo"
import { FormModalContent } from "./FormModalContent"
import { RequestDemoForm } from "./forms/RequestDemoForm"
import { SquareRightArrow } from "../../atoms/icons/SquareRightArrow"
import SmallButton from "../../atoms/buttons/SmallButton"
import ColorfulButton from "../../atoms/buttons/ColorfulButton"
import LargeButton from "../../atoms/buttons/LargeButton"
import { pushFormSubmitEvent, FORM_NAME_REQUST_DEMO } from "../../../utils/datalayer"
import SmallColorfulButton from "../../atoms/buttons/SmallColorfulButton"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import { ContactPhoneIcon } from "../../atoms/icons/ContactPhoneIcon"
import { ArrowRight } from "../../atoms/icons/ArrowRight"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../../utils/sentry"

type Props = {
  productType: "satcube_ku" | string | undefined
  buttonType?:
    | "small"
    | "text"
    | "large"
    | "colorblue"
    | "smallBlue"
    | "smallGreen"
    | "secondary"
    | "textwhite"
    | "primary"
}
interface demoRequestErrors {
  field: string
  messages: string
}

export const RequestDemoButton: React.FC<Props> = ({ productType, buttonType = "text" }) => {
  const formTitle = "Sign up for demo"
  const formDescription1 = `
    At Satcube our goal is to simplify access to satellite broadband by 
    reducing the complexity and making our products accessible. 
    During a 15 minute online meeting you get the chance to experience the 
    simplicity of the Satcube Ku whilst also getting the opportunity to get 
    any questions you may have answered by our team of experts.
  `
  const formDescription2 = `
    Simply fill out the form below to sign up for a 15-minute live demonstration over Microsoft Teams, WhatsApp, Zoom or your communication method of choice.
    We look forward to showing you the easy setup!
  `
  const formDescription3 = `
    Best wishes,
    The Satcube Team
  `
  const [isOpen, setIsOpen] = useState(false)
  const [isSent, setIsSent] = useState(false)
  const [errors, setErrors] = useState<demoRequestErrors[]>([])

  const onRequestDemoCompleted = (data: any) => {
    if (data?.requestDemo?.errors?.length > 0) {
      let errorMessages: demoRequestErrors[] = []
      data.demoRequest.errors.map((e: any) => {
        errorMessages.push({ field: e.field, messages: e.messages })
      })
      setErrors(errorMessages)
    } else {
      setIsSent(true)
      setErrors([])
      pushFormSubmitEvent(FORM_NAME_REQUST_DEMO)
    }
  }

  const onRequestDemoError = (error: ApolloError) => {
    captureExceptionWithContext(error, "error requesting demo")
    setIsSent(false)
  }

  const { requestDemo, data } = useRequestDemo({
    onCompleted: (data: any) => onRequestDemoCompleted(data),
    onError: onRequestDemoError,
  })

  const handleSubmit = (input: RequestDemoInput): Promise<void> => {
    return new Promise((resolve, reject) => {
      requestDemo({
        variables: {
          input: input,
        },
      })
      resolve()
    })
  }

  const Button: React.FC = () => {
    switch (buttonType) {
      case "text":
        return <TextButton title="Book a demo" icon={<SquareRightArrow />} onClick={() => setIsOpen(true)} />
      case "textwhite":
        return (
          <TextButton
            title="Book a demo"
            iconRight={<ArrowRight color="white" />}
            color="white"
            onClick={() => setIsOpen(true)}
          />
        )
      case "small":
        return <SmallButton title="Book a demo" onClick={() => setIsOpen(true)} />
      case "colorblue":
        return <ColorfulButton variant="blue" title="Book demo" onClick={() => setIsOpen(true)} />
      case "smallBlue":
        return <SmallColorfulButton variant="blue" title="Book demo" onClick={() => setIsOpen(true)} />
      case "smallGreen":
        return <SmallColorfulButton variant="green" title="Book demo" onClick={() => setIsOpen(true)} />
      case "secondary":
        return (
          <SecondaryButton
            title="Book a demo"
            icon={<ContactPhoneIcon color="white" />}
            onClick={() => setIsOpen(true)}
          />
        )
      case "primary":
        return <PrimaryButton title="Book a demo" icon={<ContactPhoneIcon />} onClick={() => setIsOpen(true)} />
      default:
        return <LargeButton title="Book a demo" icon={<SquareRightArrow />} onClick={() => setIsOpen(true)} />
    }
  }

  return (
    <>
      <Button />
      {isOpen &&
        createPortal(
          <AnimatePresence mode="wait">
            <Modal open={isOpen} onClose={() => setIsOpen(false)} closeIcon>
              <FormModalContent
                formTitle={formTitle}
                formDescription1={formDescription1}
                formDescription2={formDescription2}
                formDescription3={formDescription3}
              >
                <RequestDemoForm
                  handleSubmit={handleSubmit}
                  productType={productType}
                  isSent={isSent}
                  setIsSent={setIsSent}
                  errors={errors}
                />
              </FormModalContent>
            </Modal>
          </AnimatePresence>,
          document.body,
        )}
    </>
  )
}
